import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column
} from '@progress/kendo-react-grid';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, useMemo } from 'react';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { LinkCell } from '../components/grid';
import { UsersCardView } from './users-cardview';
import Toolbar from '../components/page-toolbar';
import { Permission } from '../types/permission';
import { ColumnMenu } from '../components/columnMenu';
import { Loader } from '@progress/kendo-react-indicators';
import { NoRecords } from '../components/grid/no-records';
import RoleBasedButton from '../components/role-based-button';
import { DefaultGridSettings } from '../constants/grid-settings';
import { useGetFacilitatorUsers } from '../hooks/services/use-users';
import { CardViewDataStateChangeEvent } from './organisations-cardview';

const initialDataState: State = {
  sort: [{ field: 'username', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const FacilitatorsGrid = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');

  const isAuthorizedManageFacilitator = (
    auth.user?.profile.role as string[]
  ).find((r) => r === 'OrgAdmin.ManageFacilitators');

  const { data, isLoading, mutate } = useGetFacilitatorUsers(
    toDataSourceRequestString(dataState)
  );

  const exportData = useMemo(
    () =>
      (data?.data ?? []).map((item) => ({
        ...item,
        roles: Array.isArray(item.roles)
          ? item.roles.map((r: string) => `${r}`).join(', ')
          : item.roles
      })),
    [data]
  );

  const LinkedUsernameCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props}></LinkCell>
  );

  const permissionColumn = (props: GridCellProps) => {
    const roles = props.dataItem.roles;

    return (
      <td className={'k-table-td'}>
        <ul>
          {roles.map((r: string) => (
            <li>{r}</li>
          ))}
        </ul>
      </td>
    );
  };

  const onAddFacilitator = () => {
    navigate('/facilitators/invite-facilitator');
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar title={'Facilitators'}>
        <RoleBasedButton
          requiredRoles={[Permission['OrgAdmin.ManageFacilitators']]}
          label={'Add new'}
          onClick={onAddFacilitator}
        />
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search facilitators'}
          columnsToSearch={['username', 'fullName', 'roles']}
          showCardMode={true}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={exportData}
          ref={gridExportRef}
          fileName={'OrganisationFacilitators.xlsx'}>
          <ExcelExportColumn field="username" title="Email" width={300} />
          <ExcelExportColumn field="fullName" title="Name" width={200} />
          <ExcelExportColumn
            field="roles"
            title="Assigned Permissions"
            width={200}
          />
        </ExcelExport>

        {gridMode === 'grid' && (
          <Grid
            id={'grid-user'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'username'}
              filter={'text'}
              title={'Email'}
              columnMenu={ColumnMenu}
              {...(isAuthorizedManageFacilitator && {
                cells: { data: LinkedUsernameCell }
              })}
            />
            <Column field={'fullName'} title={'Name'} columnMenu={ColumnMenu} />
            <Column
              field={'roles'}
              title={'Assigned Permissions'}
              columnMenu={ColumnMenu}
              cells={{ data: (props) => permissionColumn(props) }}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        )}
        {gridMode === 'card' && (
          <UsersCardView
            data={data?.data ?? []}
            onUsernameClick={(id: number) => navigate(`/users/${id}`)}
            total={data?.total}
            {...dataState}
            onDataStateChange={(e: CardViewDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
          />
        )}
      </div>
    </>
  );
};
export default FacilitatorsGrid;
